import { useEffect, useState, useMemo } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../components/ui/dialog';
import { Button } from '../components/ui/button';
import { Label } from '../components/ui/label';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Checkbox } from '../components/ui/checkbox';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Loader2, Users, ArrowRightLeft, Plus, X, AlertTriangle, CheckCircle2 } from 'lucide-react';
import { toast } from 'sonner';
import { logger } from '../utils/logger';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Ownership summary sub-panel
function OwnershipSummaryCard({ summary }) {
  if (!summary) return null;
  const rows = [
    ['Leads', summary.leads_total, 'leads-count'],
    ['Open Deals', summary.deals_open, 'deals-count'],
    ['Open Tasks', summary.tasks_open, 'tasks-count'],
    ['Upcoming Appointments', summary.appointments_open, 'appts-count'],
    ['Open Contracts', summary.contracts_open, 'contracts-count'],
  ];
  return (
    <div className="p-4 border rounded-lg bg-slate-50" data-testid="ownership-summary">
      <div className="text-xs uppercase tracking-wide font-semibold text-slate-500 mb-2">
        Current ownership — {summary.name}
      </div>
      <div className="grid grid-cols-2 sm:grid-cols-5 gap-3">
        {rows.map(([label, value, tid]) => (
          <div key={label} className="text-center">
            <div className="text-2xl font-bold text-slate-800" data-testid={tid}>{value}</div>
            <div className="text-xs text-slate-500">{label}</div>
          </div>
        ))}
      </div>
    </div>
  );
}

/**
 * Reusable Bulk Transfer modal.
 * Props:
 *  - open, onClose
 *  - sourceUser: { user_id, name } — user whose leads are being transferred
 *  - allUsers: full users array (used to build target options)
 *  - onSuccess(result): called after successful transfer
 *  - forceDeactivate?: bool — pre-check the "Deactivate source after" box (used by delete flow)
 */
export default function TransferLeadsModal({ open, onClose, sourceUser, allUsers, onSuccess, forceDeactivate = false }) {
  const [summary, setSummary] = useState(null);
  const [loadingSummary, setLoadingSummary] = useState(false);
  const [mode, setMode] = useState('all'); // all | round_robin | percentage | filter | manual
  const [targets, setTargets] = useState([{ user_id: '', percentage: 100 }]);
  const [reason, setReason] = useState('');
  const [scope, setScope] = useState({ deals: true, tasks: true, appointments: true });
  const [deactivate, setDeactivate] = useState(forceDeactivate);
  const [busy, setBusy] = useState(false);
  const [filters, setFilters] = useState({ stages: '', tags: '' });
  const [result, setResult] = useState(null);

  // AE-role users only for targets (per user's requirement)
  const targetCandidates = useMemo(
    () => (allUsers || []).filter(
      (u) => u.user_id !== sourceUser?.user_id
        && u.role === 'account_executive'
        && u.is_active !== false,
    ),
    [allUsers, sourceUser],
  );

  // Fetch ownership summary when modal opens
  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      if (!open || !sourceUser?.user_id) return;
      setLoadingSummary(true);
      try {
        const r = await fetch(`${API_URL}/api/users/${sourceUser.user_id}/ownership-summary`, {
          credentials: 'include',
        });
        if (r.ok) {
          const d = await r.json();
          if (!cancelled) setSummary(d);
        }
      } catch (err) {
        logger.error('Ownership summary fetch failed', err);
      } finally {
        if (!cancelled) setLoadingSummary(false);
      }
    };
    load();
    return () => { cancelled = true; };
  }, [open, sourceUser]);

  // Reset on close
  useEffect(() => {
    if (!open) {
      setMode('all');
      setTargets([{ user_id: '', percentage: 100 }]);
      setReason('');
      setScope({ deals: true, tasks: true, appointments: true });
      setDeactivate(forceDeactivate);
      setFilters({ stages: '', tags: '' });
      setResult(null);
      setSummary(null);
    }
  }, [open, forceDeactivate]);

  // Ensure deactivate reflects forceDeactivate whenever the modal is (re)opened
  useEffect(() => {
    if (open) {
      setDeactivate(forceDeactivate);
    }
  }, [open, forceDeactivate]);

  const addTarget = () => {
    setTargets((prev) => {
      const equalShare = Math.floor(100 / (prev.length + 1));
      const remainder = 100 - equalShare * (prev.length + 1);
      const nextTargets = prev.map((t) => ({ ...t, percentage: equalShare }));
      nextTargets.push({ user_id: '', percentage: equalShare + remainder });
      return nextTargets;
    });
  };

  const removeTarget = (idx) => {
    setTargets((prev) => {
      const next = prev.filter((_, i) => i !== idx);
      // Redistribute percentages evenly
      const equal = Math.floor(100 / Math.max(next.length, 1));
      const remainder = 100 - equal * next.length;
      return next.map((t, i) => ({ ...t, percentage: i === next.length - 1 ? equal + remainder : equal }));
    });
  };

  const updateTarget = (idx, field, value) => {
    setTargets((prev) => prev.map((t, i) => (i === idx ? { ...t, [field]: value } : t)));
  };

  const validate = () => {
    if (!targets.length) return 'Add at least one target user';
    for (const t of targets) {
      if (!t.user_id) return 'Every target row must have a user selected';
    }
    // Uniqueness
    const ids = targets.map((t) => t.user_id);
    if (new Set(ids).size !== ids.length) return 'Cannot transfer to the same user twice';
    if (mode === 'percentage') {
      const sum = targets.reduce((s, t) => s + Number(t.percentage || 0), 0);
      if (Math.round(sum) !== 100) return `Percentages must sum to 100 (currently ${sum})`;
    }
    return null;
  };

  const handleSubmit = async () => {
    const err = validate();
    if (err) { toast.error(err); return; }
    setBusy(true);
    try {
      const body = {
        source_user_id: sourceUser?.user_id,
        mode,
        targets: targets.map((t) => ({ user_id: t.user_id, percentage: Number(t.percentage) || 0 })),
        transfer_scope: scope,
        reason: reason.trim() || undefined,
        deactivate_source_after: deactivate,
      };
      if (mode === 'filter') {
        body.filters = {
          stages: filters.stages ? filters.stages.split(',').map((s) => s.trim()).filter(Boolean) : undefined,
          tags: filters.tags ? filters.tags.split(',').map((s) => s.trim()).filter(Boolean) : undefined,
        };
      }
      const r = await fetch(`${API_URL}/api/leads/transfer`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });
      const d = await r.json().catch(() => ({}));
      if (!r.ok) {
        toast.error(d.detail || 'Transfer failed');
        return;
      }
      setResult(d);
      toast.success(
        `${d.leads_transferred} lead${d.leads_transferred === 1 ? '' : 's'} transferred`
        + (d.source_deactivated ? ' — source user deactivated' : ''),
      );
      onSuccess && onSuccess(d);
    } catch (e) {
      logger.error('Transfer failed', e);
      toast.error('Transfer failed');
    } finally {
      setBusy(false);
    }
  };

  const percentSum = targets.reduce((s, t) => s + Number(t.percentage || 0), 0);

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="transfer-leads-modal">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <ArrowRightLeft className="w-5 h-5 text-amber-600" />
            Transfer Leads
          </DialogTitle>
          <DialogDescription>
            Move ownership of leads (and related open deals / tasks / appointments)
            {sourceUser?.name ? ` from ${sourceUser.name}` : ''} to one or more Account Executives.
            Stage and activity history are preserved.
          </DialogDescription>
        </DialogHeader>

        {result ? (
          <div className="space-y-4 py-2" data-testid="transfer-result">
            <div className="flex items-center gap-2 text-green-700">
              <CheckCircle2 className="w-6 h-6" />
              <span className="text-base font-semibold">Transfer Complete</span>
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 text-center">
              <SummaryStat label="Leads moved" value={result.leads_transferred} tid="result-leads" />
              <SummaryStat label="Deals" value={result.deals_reassigned} tid="result-deals" />
              <SummaryStat label="Tasks" value={result.tasks_reassigned} tid="result-tasks" />
              <SummaryStat label="Appts" value={result.appointments_reassigned} tid="result-appts" />
            </div>
            <div className="text-sm text-slate-600">
              Batch ID: <span className="font-mono text-xs">{result.batch_id}</span>
            </div>
            {result.source_deactivated && (
              <div className="p-3 bg-amber-50 border border-amber-200 rounded text-sm text-amber-800">
                Source user has been deactivated. You can now hard-delete their account from the Users page.
              </div>
            )}
            <DialogFooter>
              <Button onClick={onClose} data-testid="transfer-done">Done</Button>
            </DialogFooter>
          </div>
        ) : (
          <div className="space-y-5 py-2">
            {loadingSummary ? (
              <div className="flex items-center gap-2 text-slate-500 text-sm">
                <Loader2 className="w-4 h-4 animate-spin" /> Loading ownership...
              </div>
            ) : (
              <OwnershipSummaryCard summary={summary} />
            )}

            {/* Mode selector */}
            <div>
              <Label className="text-sm font-medium">Transfer strategy</Label>
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 mt-2">
                {[
                  ['all', 'All → 1 AE'],
                  ['round_robin', 'Round Robin'],
                  ['percentage', 'Percentage Split'],
                  ['filter', 'Filter (Stage/Tag)'],
                ].map(([val, label]) => (
                  <button
                    key={val}
                    type="button"
                    onClick={() => setMode(val)}
                    data-testid={`transfer-mode-${val}`}
                    className={`text-xs px-3 py-2 rounded border transition ${
                      mode === val
                        ? 'bg-amber-500 text-white border-amber-500'
                        : 'bg-white text-slate-700 border-slate-200 hover:border-amber-300'
                    }`}
                  >
                    {label}
                  </button>
                ))}
              </div>
              <p className="text-xs text-slate-500 mt-2">
                {mode === 'all' && 'All leads go to a single target user (add one target below).'}
                {mode === 'round_robin' && 'Leads are distributed evenly across every target user in turn.'}
                {mode === 'percentage' && 'Split by percentage — you control how much each target gets.'}
                {mode === 'filter' && 'Only leads matching the filter (stage/tag) are moved. All matches go to the target(s) via round-robin.'}
              </p>
            </div>

            {/* Filter mode extras */}
            {mode === 'filter' && (
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div>
                  <Label className="text-xs">Stages (comma separated)</Label>
                  <Input
                    value={filters.stages}
                    onChange={(e) => setFilters({ ...filters, stages: e.target.value })}
                    placeholder="e.g. cold_data, warm, negotiation"
                    data-testid="transfer-filter-stages"
                  />
                </div>
                <div>
                  <Label className="text-xs">Tags (comma separated)</Label>
                  <Input
                    value={filters.tags}
                    onChange={(e) => setFilters({ ...filters, tags: e.target.value })}
                    placeholder="e.g. VIP, Booked"
                    data-testid="transfer-filter-tags"
                  />
                </div>
              </div>
            )}

            {/* Target list */}
            <div>
              <div className="flex items-center justify-between mb-2">
                <Label className="text-sm font-medium">Target account executive(s)</Label>
                {mode !== 'all' && (
                  <Button type="button" size="sm" variant="outline" onClick={addTarget} data-testid="add-target-btn">
                    <Plus className="w-3 h-3 mr-1" /> Add target
                  </Button>
                )}
              </div>
              <div className="space-y-2">
                {(mode === 'all' ? targets.slice(0, 1) : targets).map((t, idx) => (
                  <div key={idx} className="flex items-center gap-2" data-testid={`target-row-${idx}`}>
                    <Select value={t.user_id} onValueChange={(v) => updateTarget(idx, 'user_id', v)}>
                      <SelectTrigger className="flex-1" data-testid={`target-user-${idx}`}>
                        <SelectValue placeholder="Select AE..." />
                      </SelectTrigger>
                      <SelectContent>
                        {targetCandidates.length === 0 && (
                          <div className="px-3 py-2 text-xs text-slate-500">
                            No active Account Executives available.
                          </div>
                        )}
                        {targetCandidates.map((u) => (
                          <SelectItem key={u.user_id} value={u.user_id}>
                            {u.name} <span className="text-slate-400 text-xs">({u.email})</span>
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    {mode === 'percentage' && (
                      <div className="flex items-center gap-1">
                        <Input
                          type="number"
                          min="0"
                          max="100"
                          value={t.percentage}
                          onChange={(e) => updateTarget(idx, 'percentage', e.target.value)}
                          className="w-20"
                          data-testid={`target-percent-${idx}`}
                        />
                        <span className="text-xs text-slate-500">%</span>
                      </div>
                    )}
                    {targets.length > 1 && mode !== 'all' && (
                      <button
                        type="button"
                        onClick={() => removeTarget(idx)}
                        className="text-slate-400 hover:text-red-600"
                        data-testid={`remove-target-${idx}`}
                        aria-label="Remove target"
                      >
                        <X className="w-4 h-4" />
                      </button>
                    )}
                  </div>
                ))}
              </div>
              {mode === 'percentage' && (
                <div className={`text-xs mt-1 ${Math.round(percentSum) === 100 ? 'text-slate-500' : 'text-red-600 font-medium'}`}>
                  Total: {percentSum}% {Math.round(percentSum) !== 100 && '— must equal 100'}
                </div>
              )}
            </div>

            {/* Scope */}
            <div>
              <Label className="text-sm font-medium">Also reassign</Label>
              <div className="flex flex-wrap gap-4 mt-2">
                <label className="flex items-center gap-2 cursor-pointer text-sm">
                  <Checkbox
                    checked={scope.deals}
                    onCheckedChange={(v) => setScope({ ...scope, deals: !!v })}
                    data-testid="scope-deals"
                  />
                  Open Deals
                </label>
                <label className="flex items-center gap-2 cursor-pointer text-sm">
                  <Checkbox
                    checked={scope.tasks}
                    onCheckedChange={(v) => setScope({ ...scope, tasks: !!v })}
                    data-testid="scope-tasks"
                  />
                  Open Tasks (linked to these leads)
                </label>
                <label className="flex items-center gap-2 cursor-pointer text-sm">
                  <Checkbox
                    checked={scope.appointments}
                    onCheckedChange={(v) => setScope({ ...scope, appointments: !!v })}
                    data-testid="scope-appointments"
                  />
                  Upcoming Appointments
                </label>
              </div>
            </div>

            {/* Reason */}
            <div>
              <Label className="text-sm font-medium">Reason (optional — appears in the lead audit log)</Label>
              <Textarea
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                placeholder="e.g. Alice left the company on 2026-02-28 — reassigning her book of business to Bob"
                rows={2}
                data-testid="transfer-reason"
              />
            </div>

            {/* Deactivate */}
            <label className="flex items-start gap-2 cursor-pointer text-sm p-3 border rounded-lg bg-amber-50">
              <Checkbox
                checked={deactivate}
                onCheckedChange={(v) => setDeactivate(!!v)}
                data-testid="deactivate-source"
              />
              <div>
                <div className="font-medium text-slate-800">Deactivate {sourceUser?.name || 'the source user'} after transfer</div>
                <div className="text-xs text-slate-500">
                  If this transfer leaves them with zero owned leads/deals/tasks/appointments, we&apos;ll set their account
                  to inactive and invalidate their sessions. You can hard-delete them from the Users page afterwards.
                </div>
              </div>
            </label>

            {summary && summary.total_owned === 0 && (
              <div className="p-3 border border-blue-200 bg-blue-50 rounded text-sm text-blue-800 flex items-start gap-2">
                <AlertTriangle className="w-4 h-4 mt-0.5 shrink-0" />
                This user currently owns no active leads/deals/tasks/appointments — nothing to transfer.
                You may deactivate and hard-delete their account from the Users page.
              </div>
            )}

            <DialogFooter>
              <Button variant="outline" onClick={onClose} disabled={busy} data-testid="transfer-cancel">
                Cancel
              </Button>
              <Button
                onClick={handleSubmit}
                disabled={busy || targetCandidates.length === 0 || (summary && summary.total_owned === 0)}
                className="bg-amber-600 hover:bg-amber-700"
                data-testid="transfer-submit"
              >
                {busy ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Users className="w-4 h-4 mr-2" />}
                Transfer Leads
              </Button>
            </DialogFooter>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

function SummaryStat({ label, value, tid }) {
  return (
    <div className="p-3 bg-slate-50 rounded">
      <div className="text-2xl font-bold text-slate-800" data-testid={tid}>{value ?? 0}</div>
      <div className="text-xs text-slate-500">{label}</div>
    </div>
  );
}
